/**
 * Bond Mentor — Bond Vigilante Data (Drop 3)
 *
 * Tracks bond vigilante activity, Treasury auction results,
 * foreign holdings trends, and term premium data.
 */

// ============================================
// VIGILANTE ACTIVITY LEVELS
// ============================================
export const VIGILANTE_LEVELS = {
  LOW: {
    key: 'LOW',
    label: 'Low',
    emoji: '😴',
    color: '#22c55e',
    description: 'Bond vigilantes are quiet. No significant selling pressure on government debt. Fiscal and monetary policy are broadly accepted by the market. This is a calm environment — no threat from this angle.',
  },
  MODERATE: {
    key: 'MODERATE',
    label: 'Moderate',
    emoji: '👀',
    color: '#eab308',
    description: 'Vigilantes are starting to pay attention. Some rumblings of discontent about fiscal policy, deficit spending, or inflation management. They have not taken major action yet, but they are watching closely.',
  },
  HIGH: {
    key: 'HIGH',
    label: 'High',
    emoji: '⚡',
    color: '#f97316',
    description: 'Vigilantes are actively selling government bonds, pushing yields higher. They are punishing what they see as irresponsible fiscal policy or inadequate inflation control. This raises borrowing costs for everyone and creates headwinds for stocks.',
  },
  EXTREME: {
    key: 'EXTREME',
    label: 'Extreme',
    emoji: '🔥',
    color: '#ef4444',
    description: 'Full-blown vigilante revolt. Massive selling of government bonds. Yields spiking uncontrollably. The market has lost confidence in fiscal or monetary leadership. This is a crisis-level event that historically precedes major market dislocations.',
  },
};

// ============================================
// AUCTION HEALTH RATINGS
// ============================================
export const AUCTION_RATINGS = {
  STRONG: { label: 'Strong', color: '#22c55e', emoji: '✅' },
  ADEQUATE: { label: 'Adequate', color: '#3b82f6', emoji: '👍' },
  WEAK: { label: 'Weak', color: '#f97316', emoji: '⚠️' },
  FAILED: { label: 'Failed', color: '#ef4444', emoji: '🚨' },
};

// ============================================
// VIGILANTE DATA PER SCENARIO
// ============================================
export const VIGILANTE_SCENARIOS = {
  ALL_CLEAR: {
    activityLevel: 'LOW',
    activityScore: 15,  // 0-100

    // What are they reacting to?
    triggers: [
      {
        name: 'Deficit Spending',
        severity: 'low',
        color: '#22c55e',
        note: 'Government deficit is within normal range. No major spending concerns.',
      },
      {
        name: 'Inflation Credibility',
        severity: 'low',
        color: '#22c55e',
        note: 'Fed has maintained inflation credibility. Markets trust the target will be met.',
      },
      {
        name: 'Debt Issuance',
        severity: 'low',
        color: '#22c55e',
        note: 'Treasury issuance schedule is manageable. No flood of new supply overwhelming demand.',
      },
    ],

    // Treasury Auction Results
    auctions: [
      { date: new Date(2026, 2, 24), type: '10-Year Note', bidToCover: 2.55, rating: 'STRONG', tailBps: -0.5, note: 'Excellent demand. Priced through when-issued. No tail.' },
      { date: new Date(2026, 2, 20), type: '30-Year Bond', bidToCover: 2.42, rating: 'ADEQUATE', tailBps: 0.2, note: 'Solid demand. Small tail but well within normal range.' },
      { date: new Date(2026, 2, 18), type: '2-Year Note', bidToCover: 2.68, rating: 'STRONG', tailBps: -0.3, note: 'Strong front-end demand. Short-duration appetite remains healthy.' },
      { date: new Date(2026, 2, 13), type: '10-Year Note', bidToCover: 2.51, rating: 'STRONG', tailBps: 0.0, note: 'On the screws. Textbook auction.' },
      { date: new Date(2026, 2, 11), type: '3-Year Note', bidToCover: 2.60, rating: 'STRONG', tailBps: -0.2, note: 'Very strong. Dealers took minimal share — real money buyers stepped up.' },
    ],

    // Foreign Holdings
    foreignHoldings: {
      trend: 'STABLE',
      trendColor: '#3b82f6',
      trendLabel: 'Stable',
      topHolders: [
        { country: 'Japan', amount: '$1.08T', change: '+$2B', direction: 'up' },
        { country: 'China', amount: '$0.77T', change: '-$1B', direction: 'flat' },
        { country: 'UK', amount: '$0.72T', change: '+$5B', direction: 'up' },
        { country: 'Luxembourg', amount: '$0.40T', change: '+$1B', direction: 'up' },
      ],
      note: 'Foreign central banks are holding steady. No sign of significant selling. Japan and UK modestly increasing positions.',
    },

    // Term Premium
    termPremium: {
      current: 0.15,
      average: 0.35,
      trend: 'STABLE',
      trendColor: '#22c55e',
      note: 'Term premium is low and stable, indicating investors are comfortable holding long-term bonds without demanding much extra compensation. This suggests low perceived risk.',
    },

    // Summary
    sideToBeOn: 'Vigilantes are quiet — risk assets are safe from this angle. No action needed based on vigilante signals. Stay with your current positioning.',
    analysis: 'Bond vigilante activity is minimal. Treasury auctions are well-received, foreign holders are stable, and the term premium is low. There is no protest from the bond market about government fiscal policy. This is a supportive backdrop for stocks.',
  },

  EARLY_WARNING: {
    activityLevel: 'MODERATE',
    activityScore: 42,

    triggers: [
      {
        name: 'Deficit Spending',
        severity: 'medium',
        color: '#eab308',
        note: 'Government deficit widening beyond projections. Bond market starting to question the trajectory.',
      },
      {
        name: 'Inflation Credibility',
        severity: 'medium',
        color: '#eab308',
        note: 'Inflation staying above target longer than expected. Some doubts about Fed commitment to 2% target.',
      },
      {
        name: 'Debt Issuance',
        severity: 'high',
        color: '#f97316',
        note: 'Treasury ramping up issuance to fund the deficit. Market having to absorb significantly more supply.',
      },
    ],

    auctions: [
      { date: new Date(2026, 2, 24), type: '10-Year Note', bidToCover: 2.28, rating: 'WEAK', tailBps: 1.8, note: 'Soft demand. 1.8bp tail — buyers demanding a premium to participate.' },
      { date: new Date(2026, 2, 20), type: '30-Year Bond', bidToCover: 2.15, rating: 'WEAK', tailBps: 2.5, note: 'Disappointing. Long-end buyers stepping back. 2.5bp tail is concerning.' },
      { date: new Date(2026, 2, 18), type: '2-Year Note', bidToCover: 2.52, rating: 'ADEQUATE', tailBps: 0.3, note: 'Front-end still okay, but not as strong as recent averages.' },
      { date: new Date(2026, 2, 13), type: '10-Year Note', bidToCover: 2.35, rating: 'ADEQUATE', tailBps: 0.8, note: 'Below average. Trend of weakening demand is developing.' },
      { date: new Date(2026, 2, 11), type: '3-Year Note', bidToCover: 2.48, rating: 'ADEQUATE', tailBps: 0.5, note: 'Passable but declining from recent highs.' },
    ],

    foreignHoldings: {
      trend: 'DECLINING',
      trendColor: '#f97316',
      trendLabel: 'Gradual Decline',
      topHolders: [
        { country: 'Japan', amount: '$1.05T', change: '-$8B', direction: 'down' },
        { country: 'China', amount: '$0.74T', change: '-$12B', direction: 'down' },
        { country: 'UK', amount: '$0.71T', change: '-$3B', direction: 'down' },
        { country: 'Luxembourg', amount: '$0.39T', change: '-$2B', direction: 'down' },
      ],
      note: 'Foreign holders are quietly reducing positions. China continues trimming. Japan also pulling back. This means more supply must be absorbed by domestic buyers.',
    },

    termPremium: {
      current: 0.65,
      average: 0.35,
      trend: 'RISING',
      trendColor: '#f97316',
      note: 'Term premium is rising above the historical average. Investors are demanding more compensation to hold long-term bonds — a sign of growing uncertainty about the fiscal outlook and future inflation.',
    },

    sideToBeOn: 'Vigilantes are stirring — be cautious with long-duration bonds and rate-sensitive stocks. Consider reducing exposure to long-dated Treasury bonds and utilities.',
    analysis: 'Bond vigilante activity is elevated and building. Treasury auction demand is weakening, foreign holders are trimming, and the term premium is rising. The market is sending early signals of displeasure with fiscal policy direction. This is not yet a crisis, but the trajectory is concerning. Watch auction results closely — if the next 10-Year or 30-Year auctions tail badly, it will confirm the vigilante pressure is intensifying.',
  },

  DANGER_ZONE: {
    activityLevel: 'EXTREME',
    activityScore: 82,

    triggers: [
      {
        name: 'Deficit Spending',
        severity: 'extreme',
        color: '#ef4444',
        note: 'Government deficit has ballooned to unsustainable levels. Bond market has lost patience with fiscal indiscipline.',
      },
      {
        name: 'Inflation Credibility',
        severity: 'extreme',
        color: '#ef4444',
        note: 'Fed has lost inflation credibility. Market no longer believes the 2% target will be achieved. Inflation expectations unanchored.',
      },
      {
        name: 'Debt Issuance',
        severity: 'extreme',
        color: '#ef4444',
        note: 'Massive Treasury issuance required. Market cannot absorb the supply without significantly higher yields.',
      },
      {
        name: 'Political Dysfunction',
        severity: 'high',
        color: '#f97316',
        note: 'Political gridlock preventing any fiscal reform. No credible plan to address the deficit. Vigilantes see no path to improvement.',
      },
    ],

    auctions: [
      { date: new Date(2026, 2, 24), type: '10-Year Note', bidToCover: 1.92, rating: 'FAILED', tailBps: 4.5, note: 'UGLY. Massive 4.5bp tail. Dealers forced to absorb unsold bonds. Buyers boycotting.' },
      { date: new Date(2026, 2, 20), type: '30-Year Bond', bidToCover: 1.85, rating: 'FAILED', tailBps: 5.2, note: 'DISASTROUS. Worst 30-year auction in years. Long-end demand has evaporated.' },
      { date: new Date(2026, 2, 18), type: '2-Year Note', bidToCover: 2.25, rating: 'WEAK', tailBps: 1.5, note: 'Even the front end is struggling. Flight from all government maturities.' },
      { date: new Date(2026, 2, 13), type: '10-Year Note', bidToCover: 2.02, rating: 'WEAK', tailBps: 3.1, note: 'Persistent weakness. Third consecutive soft 10-Year auction.' },
      { date: new Date(2026, 2, 11), type: '3-Year Note', bidToCover: 2.30, rating: 'WEAK', tailBps: 1.2, note: 'Even short-duration auctions losing demand.' },
    ],

    foreignHoldings: {
      trend: 'SELLING',
      trendColor: '#ef4444',
      trendLabel: 'Active Selling',
      topHolders: [
        { country: 'Japan', amount: '$0.98T', change: '-$25B', direction: 'down' },
        { country: 'China', amount: '$0.68T', change: '-$30B', direction: 'down' },
        { country: 'UK', amount: '$0.65T', change: '-$18B', direction: 'down' },
        { country: 'Luxembourg', amount: '$0.35T', change: '-$10B', direction: 'down' },
      ],
      note: 'CRITICAL: Major foreign holders are aggressively selling U.S. Treasuries. China and Japan — the two largest holders — are both dumping. This forces yields higher and drains confidence in U.S. government debt.',
    },

    termPremium: {
      current: 1.45,
      average: 0.35,
      trend: 'SPIKING',
      trendColor: '#ef4444',
      note: 'Term premium has exploded to levels not seen since the fiscal crises of prior decades. Investors are demanding massive extra compensation to hold long-term government bonds. This is the bond market screaming about fiscal risk.',
    },

    sideToBeOn: 'Vigilantes are in full revolt — be on THEIR side. Avoid long-duration bonds. Avoid rate-sensitive stocks. Hold cash and short-term Treasuries only. Do NOT fight the vigilantes.',
    analysis: 'This is a full-blown bond vigilante crisis. Treasury auctions are failing, foreign holders are dumping at the fastest pace in years, and the term premium has spiked to crisis levels. The bond market has effectively lost confidence in government fiscal management. When vigilantes reach this level of activity, the consequences spill into every asset class — stocks fall, credit tightens, and even the Fed may be forced to intervene. Capital preservation is paramount. Do NOT hold long-duration bonds. Position for further yield spikes.',
  },
};
